import { useId, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AlertTriangle, Check, Clock, Info, X as XIcon, Zap } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import {
  strategyHintForTag,
  trayBandForRun,
  useReevaluationsForOverrideQuery,
  type ReevalRun,
  type TrayBand,
} from '@/lib/api/reevaluations';
import { useOverrideTaxonomyQuery } from '@/lib/api/overrides';
import { cn } from '@/lib/utils/cn';

/**
 * T-0113 / E03-F03 — AI re-evaluation tray.
 *
 * The third part of the README §8 pattern 3 round-trip (override capture
 * → re-eval tray opens automatically → user picks scope of re-route).
 *
 * Visual contract:
 *   - `design_handoff_caliber_v2/prototype/modals-round4a.js`
 *     → `Modals.openReevalTray`.
 *
 * Behavior contract:
 *   - The tray opens automatically after the override modal's `onSaved`
 *     fires. The override modal closes, then the parent opens this tray
 *     with the override id + reason tag.
 *   - The tray POLLS `GET /api/v1/engagements/:id/reevaluations?trigger_override_id=...`
 *     until the synthesizer worker registers a row. The endpoint is not
 *     yet shipped on the API (T-0113 is the UI ticket; the read surface
 *     is called out in the spec §4 but the backend tracker is open) — the
 *     tray degrades gracefully on 404 and shows the structured strategy
 *     hint locally so the user always sees something meaningful.
 *   - Once the run reaches a terminal status (`no_change` | `updated` |
 *     `inconsistency_flagged` | `failed`), polling stops and the
 *     "View audit" affordance becomes available.
 *
 * Status bands per spec §6 / prototype:
 *   - queued     : override captured, worker hasn't picked up yet
 *   - routing    : run row inserted, status='pending'
 *   - synthesizing: status='running' (LLM call active)
 *   - ready      : terminal — proposals updated, inconsistencies flagged
 *   - failed     : worker failure; the override stays canonical regardless
 *   - deferred-cross-module: cross-module run; deferred to next snapshot
 *
 * Stay-out zones (T-0113):
 *   - We do NOT mutate AI proposals here — the tray is read-only.
 *   - We do NOT navigate to the audit-trail route on click — we just
 *     close cleanly; the parent surfaces the audit deep-link.
 *
 * Dismiss rules:
 *   - ESC closes (Radix).
 *   - Click-outside closes (no mid-write to protect).
 */

export interface ProposedUpdate {
  /** Stable id — used as the React key + the chip's data-cite-id. */
  id: string;
  /** Short label e.g. "R-019". */
  shortId: string;
  /** Title of the related record. */
  title: string;
  /** Kind of record — drives the icon + label. */
  kind: 'risk' | 'rfi' | 'finding' | 'criterion' | 'milestone' | 'deliverable';
  /** Optional updated confidence after re-eval (0..1). */
  confidence?: number | null;
  /** Optional citations supporting the updated proposal. */
  citations?: CitationChipData[];
  /** Optional movement label, e.g. "+0.07" or "flagged". */
  delta?: string;
  /**
   * When true, the row renders the inconsistency flag (conflicts with an
   * existing override) per spec §11 Scenario 3.
   */
  flagged?: boolean;
}

export interface ReevalTrayModalProps {
  /** Engagement id — required for the polling query. */
  engagementId: string;
  /**
   * The override that triggered this tray. The tray polls the "runs by
   * trigger_override_id" surface until a terminal row appears.
   */
  triggerOverrideId: string;
  /** Reason tag of the triggering override — drives the strategy hint. */
  reasonTag: string;
  /**
   * Optional updated proposals to surface. Real values come from the
   * polled run row + a child join on `ai_proposals`; until the backend
   * read endpoint ships, the parent can pass `undefined` and the tray
   * renders the structured strategy hint without the per-record list.
   */
  proposedUpdates?: ProposedUpdate[];
  /** Open / close — driven by the modal context. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ReevalTrayModal({
  engagementId,
  triggerOverrideId,
  reasonTag,
  proposedUpdates,
  open,
  onOpenChange,
}: ReevalTrayModalProps) {
  const titleId = useId();
  const query = useReevaluationsForOverrideQuery(engagementId, triggerOverrideId, {
    enabled: open,
  });
  const taxonomy = useOverrideTaxonomyQuery();
  const tagLabel = useMemo(() => {
    return taxonomy.data?.tags.find((t) => t.key === reasonTag)?.label ?? reasonTag;
  }, [taxonomy.data?.tags, reasonTag]);
  const strategy = strategyHintForTag(reasonTag);

  // The most recent run for the triggering override. Backend orders by
  // `created_at desc` so `runs[0]` is the freshest. When the endpoint
  // isn't shipped yet, `data.runs` is the empty array we fall back to —
  // the tray shows the structured "audit available once T-0051 lands"
  // empty state.
  const latestRun: ReevalRun | undefined = query.data?.runs[0];
  const band = trayBandForRun(latestRun);

  // The polling query treats 404 as the "endpoint not shipped yet"
  // empty state; we surface that distinctly so the user knows the
  // strategy hint is the v1 audit trail.
  const isEndpointMissing = !query.isLoading && query.data?.runs.length === 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-scrim',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        />
        <Dialog.Content
          aria-labelledby={titleId}
          className={cn(
            // Tray docks bottom-right per prototype's `.ai-reeval-tray`
            // (`position:fixed;right:24px;bottom:24px;width:400px`). On
            // narrower viewports we center per the modal default.
            'fixed bottom-6 right-6 z-50 grid w-[min(440px,94vw)] max-h-[80vh]',
            'grid-rows-[auto,1fr,auto]',
            'overflow-hidden rounded-modal border border-ai-surface-border bg-paper-0 shadow-modal',
            'opacity-0 motion-safe:transition-opacity motion-safe:duration-150',
            'data-[state=open]:opacity-100',
          )}
        >
          <AiSurface
            as="header"
            className="flex items-start justify-between gap-3 border-b border-ai-surface-border border-l-0 border-r-0 border-t-0 px-4 py-3 pr-12"
          >
            <div className="flex min-w-0 items-start gap-3">
              <span
                aria-hidden="true"
                className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-paper-0 text-ai-ink shadow-sm"
              >
                <Zap className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0">
                <Dialog.Title id={titleId} className="text-body font-semibold text-ai-ink">
                  {bandTitle(band)}
                </Dialog.Title>
                <p className="mt-0.5 text-meta text-ink-500">
                  Triggered by override
                  <span className="ml-1 font-mono text-ink-700">
                    {triggerOverrideId.slice(0, 8)}
                  </span>
                  {' · '}
                  {tagLabel}
                </p>
              </div>
            </div>
            <Dialog.Close
              aria-label="Dismiss re-evaluation tray"
              className={cn(
                'absolute right-3 top-3 inline-flex h-7 w-7 items-center justify-center rounded-card',
                'text-ai-ink hover:bg-paper-0 hover:text-ink-900',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              <XIcon className="h-3.5 w-3.5" aria-hidden="true" />
            </Dialog.Close>
          </AiSurface>

          <div className="overflow-y-auto px-4 py-3">
            <StrategyHintBanner
              tagLabel={tagLabel}
              label={strategy.label}
              detail={strategy.detail}
              band={band}
            />

            <StatusList band={band} run={latestRun} isLoading={query.isLoading} />

            {proposedUpdates && proposedUpdates.length > 0 ? (
              <ProposedUpdatesList items={proposedUpdates} />
            ) : null}

            {isEndpointMissing ? (
              <div
                role="status"
                className="mt-3 flex items-start gap-2 rounded-card border border-dashed border-ink-200 bg-paper-50 px-3 py-2.5 text-meta text-ink-500"
              >
                <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                <div>
                  The re-evaluation's progress can't be shown here yet. It has been queued; your
                  override stands, and the strategy above is what the AI will apply.
                </div>
              </div>
            ) : null}
          </div>

          <footer className="flex items-center justify-between gap-3 border-t border-ink-100 bg-paper-50 px-4 py-2.5">
            <span className="text-meta text-ink-500">{bandFootHint(band)}</span>
            <a
              href={`/engagements/${engagementId}/audit`}
              title="Open the engagement audit trail (override + re-eval entries included)"
              className={cn(
                'inline-flex h-8 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-3 text-meta font-medium text-ink-700',
                'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300',
              )}
            >
              <Info className="h-3 w-3" aria-hidden="true" /> View audit
            </a>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Strategy hint banner — same content the prototype's tray shows at the
 * top of the body. Pure lookup from the reason tag.
 */
function StrategyHintBanner({
  tagLabel,
  label,
  detail,
  band,
}: {
  tagLabel: string;
  label: string;
  detail: string;
  band: TrayBand;
}) {
  return (
    <AiSurface
      as="div"
      className="rounded-card border border-ai-surface-border bg-ai-surface px-3 py-2.5"
    >
      <div className="flex items-center gap-1.5 text-meta">
        <span className="inline-flex items-center rounded-full bg-paper-0 px-2 py-0.5 text-[11px] font-medium text-ai-ink">
          {tagLabel}
        </span>
        <span className="font-semibold text-ai-ink">{label}</span>
      </div>
      <p className="mt-1.5 text-meta text-ink-500">
        {detail}
        {band === 'deferred-cross-module' ? (
          <span className="ml-1">· deferred to next snapshot capture (D-20)</span>
        ) : null}
      </p>
    </AiSurface>
  );
}

function StatusList({
  band,
  run,
  isLoading,
}: {
  band: TrayBand;
  run: ReevalRun | undefined;
  isLoading: boolean;
}) {
  const stages: { id: TrayBand; label: string }[] = [
    { id: 'queued', label: 'Override captured · enqueued' },
    { id: 'routing', label: 'Routing — resolving strategy + scope' },
    { id: 'synthesizing', label: 'Synthesizing — gathering related records' },
    { id: 'ready', label: 'Ready — re-evaluation complete' },
  ];
  // For cross-module runs the journey ends at "deferred to next
  // snapshot" — surface that explicitly rather than implying further
  // progression.
  const isDeferred = band === 'deferred-cross-module';
  // For failed runs the journey ends mid-flight; we render up to the
  // synthesis stage with a "failed" pill on the last touched node.
  const isFailed = band === 'failed';
  const currentIdx = isFailed ? 2 : isDeferred ? 1 : stages.findIndex((s) => s.id === band);

  return (
    <ol className="mt-3 flex flex-col gap-1.5">
      {stages.map((stage, idx) => {
        const isDone = currentIdx > idx;
        const isCurrent = currentIdx === idx;
        const isPending = currentIdx < idx;
        const showFailed = isFailed && isCurrent;
        const showDeferred = isDeferred && isCurrent;
        return (
          <li
            key={stage.id}
            className={cn(
              'grid grid-cols-[24px,1fr] items-start gap-2 text-body-sm',
              isPending && 'text-ink-500',
              isDone && 'text-ink-700',
              isCurrent && 'text-ink-900',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full',
                isDone && !showFailed && 'bg-posture-healthy-tint text-posture-healthy',
                isCurrent && !showFailed && !showDeferred && 'bg-ai-surface text-ai-ink',
                showFailed && 'bg-posture-critical-tint text-posture-critical',
                showDeferred && 'bg-posture-at-risk-tint text-posture-at-risk',
                isPending && 'bg-paper-100 text-ink-300',
              )}
            >
              {isDone && !showFailed ? (
                <Check className="h-3 w-3" />
              ) : showFailed ? (
                <AlertTriangle className="h-3 w-3" />
              ) : showDeferred ? (
                <Clock className="h-3 w-3" />
              ) : isCurrent ? (
                <span className="inline-flex">
                  <Spinner className={cn('text-ai-ink')} />
                </span>
              ) : null}
            </span>
            <div className="leading-snug">
              {stage.label}
              {isCurrent ? (
                <span className="ml-1 text-meta text-ink-500">
                  {showFailed && run?.failure_reason
                    ? `· ${run.failure_reason}`
                    : showDeferred
                      ? '· cross-module run'
                      : isLoading
                        ? '· waking the worker…'
                        : ''}
                </span>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Per-update list under the status bar. Renders the related-record
 * deltas the parent passes in. Each row shows the kind chip + title +
 * confidence + supporting citation chips.
 */
function ProposedUpdatesList({ items }: { items: ProposedUpdate[] }) {
  return (
    <div className="mt-3 border-t border-ink-100 pt-3">
      <div className="mb-1.5 text-meta uppercase tracking-wide text-ink-500">
        Proposed updates · {items.length}
      </div>
      <ul className="flex flex-col gap-2">
        {items.map((u) => (
          <li
            key={u.id}
            className={cn(
              'rounded-card border bg-paper-0 px-3 py-2',
              u.flagged
                ? 'border-posture-critical/40 bg-posture-critical-tint/30'
                : 'border-ink-100',
            )}
          >
            <div className="flex items-start gap-2">
              <span
                aria-hidden="true"
                className={cn(
                  'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                  u.flagged
                    ? 'bg-posture-critical-tint text-posture-critical'
                    : 'bg-paper-100 text-ink-500',
                )}
              >
                {u.flagged ? (
                  <AlertTriangle className="h-2.5 w-2.5" />
                ) : (
                  <span className="font-mono text-[8px] uppercase">{kindShortLabel(u.kind)}</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-mono text-[11px] text-ink-500">{u.shortId}</span>
                  <span className="truncate text-body-sm font-medium text-ink-900">{u.title}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {typeof u.confidence === 'number' ? (
                    <AiConfidenceChip value={u.confidence} />
                  ) : null}
                  {u.citations?.map((c) => (
                    <CitationChip key={c.id} cite={c} />
                  ))}
                  {u.delta ? (
                    <span
                      className={cn(
                        'text-meta',
                        u.flagged ? 'text-posture-critical' : 'text-ink-500',
                      )}
                    >
                      {u.delta}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function bandTitle(band: TrayBand): string {
  switch (band) {
    case 'queued':
      return 'Re-evaluation enqueued';
    case 'routing':
      return 'Routing re-evaluation';
    case 'synthesizing':
      return 'Caliber AI is re-evaluating';
    case 'ready':
      return 'Re-evaluation complete';
    case 'failed':
      return 'Re-evaluation failed';
    case 'deferred-cross-module':
      return 'Cross-module re-eval deferred';
  }
}

function bandFootHint(band: TrayBand): string {
  switch (band) {
    case 'queued':
    case 'routing':
      return 'Same-register sync · cross-module deferred to next snapshot';
    case 'synthesizing':
      return 'Bounded edge traversal · ≤10s p95 budget';
    case 'ready':
      return 'Override stays canonical · related records updated';
    case 'failed':
      return 'Override stays canonical · re-eval can be retried';
    case 'deferred-cross-module':
      return 'Will run on the next snapshot capture';
  }
}

function kindShortLabel(kind: ProposedUpdate['kind']): string {
  switch (kind) {
    case 'risk':
      return 'R';
    case 'rfi':
      return 'F';
    case 'finding':
      return 'I';
    case 'criterion':
      return 'C';
    case 'milestone':
      return 'M';
    case 'deliverable':
      return 'D';
  }
}
